import { CSSProperties } from "react";
import {
  imageListItemBackground, immersightBlue,
  immersightBlue10,
  immersightBlue50, immersightGrey
} from "./colors";
import {
  PRODUCT_GALLERY_TRANSITION, PRODUCT_TOOLTIP_MAX_WIDTH
} from "./constants";

/**
 * Function to fetch the predefined image list style object that is needed to initialize the ProductGallery.
 * Was extracted to this file to keep the component small and easily understandable.
 *
 * @returns the style object that can get spread into the image list.
 */
export const getImageListStyles = () => ({
  position: "relative",
  width: "100%",
  overflowY: "auto",
  paddingRight: ".2rem",
  "::-webkit-scrollbar": {
    width: ".5rem",
  },
  "::-webkit-scrollbar-track": {
    background: "#009fe300",
    borderRadius: "1rem",
  },
  "::-webkit-scrollbar-thumb": {
    background: "lightgray",
    borderRadius: "1rem",
  },
  "::-webkit-scrollbar-thumb:hover": {
    background: "gray",
  },
  scrollbarColor: "#009fe300",
  scrollbarWidth: "auto",
});

/**
 * Function to fetch the predefined image list element style object that is needed to initialize a single lazy image for the product gallery.
 * Was extracted to this file to keep the component small and easily understandable.
 *
 * @returns the style object that can get spread into the image list item.
 */
export const getImageListItemStyle = (): React.CSSProperties => ({
  objectFit: "scale-down",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  alignSelf: "center",
  justifySelf: "center",
  fontSize: ".7rem",
  textAlign: "center",
  width: "99%",
  height: "99%",
  marginTop: ".025rem",
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
  lineHeight: ".8rem",
  backgroundColor: imageListItemBackground,
});

/**
 * Function to create the style object for the productSearch TextField.
 *
 * @returns object containing the css styles for the product search text field in the ProductSearchPanel.
 */
export const getProductSearchTextFieldStyles = () => ({
  width: "90%",
  "& input": {
    fontSize: ".7rem",
    padding: ".2rem 0 .3rem",
  },
  margin: ".8rem 0 .2rem .7rem",
  "& input::placeholder": {
    color: immersightGrey,
  },
  "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
    borderBottom: `solid 1px ${immersightBlue}`,
  },
  "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error):after": {
    borderBottom: `solid 2px ${immersightBlue}`,
  },
});

/**
 * Function to create the style object to create the form control object located directly beneath the product search text field.
 *
 * @returns css style object.
 */
export const getProductSearchFormControlStyles = () => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "stretch",
  width: "100%",
  "& .Mui-focused": {
    color: "rgba(37, 37, 37, 0.9) !important",
  },
  "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
    borderBottom: `solid 2px ${immersightBlue}`,
  },
  "& .MuiInput-input:focus": {
    backgroundColor: `${immersightBlue10} !important`,
  },
});

/**
 * Function to create the style object for creating the input label inside of the form control, directly beneath the product search.
 *
 * @returns css style object.
 */
export const getProductSearchInputLabelStyles = () => ({
  transform: "translate(.6rem, .4rem) scale(.8)",
  letterSpacing: ".075rem",
  fontWeight: 600,
  fontSize: ".6rem",
  color: "rgba(97, 97, 97, 0.9)",
});

/**
 * Function to create the style object for creating the select elements inside of the product search panel.
 *
 * @returns css style object.
 */
export const getProductSearchSelectStyles = () => ({
  maxWidth: "95%",
  width: "95%",
  fontSize: ".6rem",
  padding: ".2rem .3rem",
  margin: "0 .3rem 0 .5rem",
  "& input": {
    padding: 0,
    fontSize: ".6rem",
  },
  "& input::before": {
    fontSize: ".9rem",
  },
});

/**
 * Function to generate the MenuProps that are needed for the select fields inside of the product search panel.
 *
 * @returns the MenuProps object for the select fields.
 */
export const getProductSearchSelectMenuProps = () => ({
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5,
      width: "auto",
      "& ul": {
        padding: 0,
      },
    },
  },
});

/**
 * Function to get the style object for the product image list item component.
 *
 * @returns css style object.
 */
 export const getProductImageListItemBarStyles = () => ({
  backgroundColor: "rgba(immersightGrey, 0.8)", 
  transition: PRODUCT_GALLERY_TRANSITION,
  width: "100%",
  "& .MuiImageListItemBar-titleWrap": {
    padding: ".1rem",
    width: "100%",
  },
  "& .MuiImageListItemBar-titleWrapBottom": {
    opacity: "75%",
    minHeight: "2rem",
    height: "2rem",
    transition: PRODUCT_GALLERY_TRANSITION,
  },
  "& .iconBtn": {
    transform: "translate(.1rem, .75rem) scale(0.8)",
    transition: PRODUCT_GALLERY_TRANSITION,
    color: "rgba(255, 255, 255, .8)", 
    backgroundColor: "transparent", 
  },
  "& .MuiImageListItemBar-title": {
    textAlign: "left",
    fontSize: ".7rem",
    textOverflow: "ellipsis",
    transition: PRODUCT_GALLERY_TRANSITION,
  },
  "& .MuiImageListItemBar-subtitle": {
    textAlign: "left",
    textOverflow: "ellipsis",
    transition: PRODUCT_GALLERY_TRANSITION,
    transform: "translateX(.1rem)",
    fontSize: ".5rem",
    "& .MuiTooltip-tooltip": {
      borderRadius: "12px !important", // ✅ Rounds the edges here too!
    }
  },
});


/**
 * Function to get the style object for the icon button inside of the product image list item component.
 *
 * @returns css style object.
 */
export const getProductImageListItemIconButtonStyles = () => ({
  position: "absolute",
  color: "rgba(255, 255, 255, 0.44)",
  right: 0,
  top: 0,
  transform: "translate(.2rem, .9rem) scale(.8)",
  transition: PRODUCT_GALLERY_TRANSITION,
  padding: "0",
});

/**
 * Function to get the style object for the card object directly inside of the product image list item component.
 *
 * @returns css style object.
 */
export const getProductImageListItemCardStyles = (
  productTooltipWidth: number,
) => ({
  width: productTooltipWidth,
  maxWidth: PRODUCT_TOOLTIP_MAX_WIDTH,
  padding: ".8rem .4rem .1rem",
  margin: 0,
  backgroundColor: "transparent",
});

/**
 * Function to get the style object for the card content object directly inside of the product image list item component.
 *
 * @returns css style object.
 */
export const getProductImageListItemCardContentStyles = () => ({
  margin: 0,
  padding: 0,
  "& + .div": {
    paddingBottom: "0 !important",
  },
});

/**
 * Function to get the style object for the typography objects directly inside of the product image list item component.
 *
 * @param {number} position numerical value to determine which of the typography objects is referenced.
 * @returns css style object.
 */
export const getProductImageListItemTypographyStyles = (position: number) => {
  switch (position) {
    default:
    case 0:
      return {
        fontSize: 17,
        padding: 0,
        fontWeight: 600,
        color: immersightBlue,
      };
    case 1:
      return {
        fontSize: 13,
        fontStyle: "italic",
        letterSpacing: "+.11rem",
        color: "rgba(222, 222, 222, 1)",
      };
    case 2:
      return {
        fontSize: 12,
        marginBottom: ".5rem",
        color: "rgba(222, 222, 222, 1)",
      };
    case 3:
      return {
        fontSize: 13,
        marginTop: ".6rem",
        color: "rgba(222, 222, 222, 1)",
      };
  }
};

/**
 * Function to generate the style object for the divider inside of the product image list item component.
 *
 * @param {number} position the position inside of the component. Differentiates similar objects.
 * @returns css style object.
 */
export const getProductImageListItemDividerStyles = (position: number) => {
  switch (position) {
    default:
    case 0:
      return {
        "&::before, &::after": {
          borderColor: "rgba(199, 199, 199, 1)",
        },
        color: "rgba(199, 199, 199, 1)",
        letterSpacing: "+.13rem",
        fontWeight: 900,
        marginBottom: ".5rem",
      };
    case 1:
      return {
        "&::before, &::after": {
          borderColor: "rgba(199, 199, 199, 1)",
        },
        color: "rgba(199, 199, 199, 1)",
        letterSpacing: "+.13rem",
        fontWeight: 900,
      };
  }
};

/**
 * Function to generate the style object for the product image list item.
 *
 * @param {number} galleryRowHeight height of a single row inside of the product gallery.
 * @returns css style object.
 */
export const getProductImageListItemStyles = () => ({
  cursor: "pointer",
  backgroundColor: "rgba(200, 200, 200, .4)",
  width: "100%",
  height: "100px", 
  overflow: "hidden",
  display: "flex",  
  alignItems: "center",
  justifyContent: "center",
  border: "ridge 1px gray",
  boxShadow: "0px 0px 3px -1px rgba(0,0,0,0.52)",
});


/**
 * Utility function to create the CSS property object for the select fields inside of the product search panel.
 *
 * @param {string} option the provided option for referencing.
 * @param {string[]} selectOptions the array of options to validate against.
 * @returns {CSS.Properties} a style object containing all relevant css styles.
 */
export const getSelectElementStyles = (
  option: string,
  selectOptions: string[],
): CSSProperties => ({
  fontWeight: selectOptions.indexOf(option) === -1 ? 400 : 700,
  backgroundColor:
    selectOptions.indexOf(option) === -1 ? "white" : immersightBlue50,
  fontSize: ".6rem !important",
});

/**
 * Function to check if the source of an image does exist, or if the alternative source will get used.
 *
 * @param {string} url the source of the image that needs to get checked.
 * @returns {boolean} flag to indicate if the image exists or not.
 */
export const checkIfImageExists = (url: string): boolean => {
  const img = new Image();
  img.src = url;

  if (img.complete) {
    return true;
  }
  const decide = (img.onload = () => true);
  if (decide()) {
    return true;
  }
  const decide2 = (img.onerror = () => false);
  if (!decide2) {
    return false;
  }

  return false;
};

/**
 * Function that takes a background color string (has to be hex!) and finds a complementary font color.
 * Will only take white and black as font colors to keep some uniformity.
 *
 * @param {string} backgroundColor color string representing the background color that needs a font assigned to it.
 * @returns {string} color for the text.
 */
export const getFontColorFromBackground = (backgroundColor: string): string => {
  backgroundColor = backgroundColor.substring(1);
  const r = parseInt(backgroundColor.substring(0, 2), 16); // 0 ~ 255
  const g = parseInt(backgroundColor.substring(2, 4), 16);
  const b = parseInt(backgroundColor.substring(4, 6), 16);
  const srgb = [r / 255, g / 255, b / 255];
  const x = srgb.map((i) => {
    if (i <= 0.04045) {
      return i / 12.92;
    }
    return Math.pow((i + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * x[0] + 0.7152 * x[1] + 0.0722 * x[2];
  return L > 0.179 ? "#000" : "#fff";
};

/**
 * Function that takes a background color string (has to be hex!) and finds a complementary and slightly transparent font color.
 * Both possible outcomes are basically white and black with added transparency.
 *
 * @param {string} backgroundColor color string representing the background color that needs a font assigned to it.
 * @returns {string} color for the text.
 */
export const getOverlayFontColorFromBackground = (
  backgroundColor: string,
): string => {
  const regularFontColor = getFontColorFromBackground(backgroundColor);
  if (regularFontColor === "#000") {
    return "rgba(0, 0, 0, .25)";
  }
  return "rgba(255, 255, 255, .25)";
};
